/**
 * Recharge widget suppressor.
 *
 * The Recharge "Subscribe & save / One-time purchase" widget is hidden via
 * recharge-widget-suppress.css. This script handles the form-side concern:
 * the widget injects a hidden <select name="selling_plan"> into the product
 * form and pre-selects the "subsave" radio, which would otherwise force the
 * cart-add to use a Recharge subscription plan and break the theme's
 * custom .purchase-options selector.
 *
 * Strategy:
 *   1. As soon as the widget appears in the DOM, click its "One-time
 *      purchase" radio so its internal Vue state is consistent.
 *   2. Disable / detach the Recharge selling_plan <select> from the form
 *      so it cannot submit a value. The theme's own .purchase-options
 *      <select name="selling_plan"> remains the sole source of truth.
 *   3. Re-run on every Vue re-render via a MutationObserver so changes
 *      from variant swaps or Recharge SDK updates don't regress the state.
 */

(() => {
  // Idempotency guard — if multiple copies of this script load, only one wires up.
  if (window.__rechargeWidgetSuppressorInstalled) return;
  window.__rechargeWidgetSuppressorInstalled = true;

  const RECHARGE_ROOT_SELECTOR = '.rc-container-wrapper, .rc_container_wrapper, [data-widget-container-wrapper]';
  const ONETIME_RADIO_SELECTOR = 'input[data-radio-onetime], input[value="onetime"]';
  const RECHARGE_PLAN_SELECT_SELECTOR = 'select[data-plans-dropdown], select[name="selling_plan"][data-v-335eb9f1]';

  /**
   * Find every Recharge widget root currently in the document and
   * neutralise it.
   */
  function neutraliseRechargeWidgets(root = document) {
    const widgets = root.querySelectorAll(RECHARGE_ROOT_SELECTOR);
    widgets.forEach(neutraliseWidget);
  }

  /**
   * Neutralise one Recharge widget instance.
   * @param {Element} widget
   */
  function neutraliseWidget(widget) {
    // 1. Click the "One-time purchase" radio so the widget's own Vue
    //    state is internally consistent (matches what we're submitting).
    const onetimeRadio = widget.querySelector(ONETIME_RADIO_SELECTOR);
    if (onetimeRadio instanceof HTMLInputElement && !onetimeRadio.checked) {
      // Use .click() rather than setting .checked so Vue sees the change
      // event and updates its internal model. If Vue is missing or the
      // click fails silently, fall back to setting .checked + dispatching.
      try {
        onetimeRadio.click();
      } catch (_) {
        onetimeRadio.checked = true;
        onetimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // 2. Detach the Recharge selling_plan <select> from the form so it
    //    cannot submit a selling_plan value. We rename the `name`
    //    attribute and disable the element.
    //
    //    Using `disabled` alone would work, but the widget's Vue
    //    instance may re-enable it on re-render, so renaming is safer.
    const planSelect = widget.querySelector(RECHARGE_PLAN_SELECT_SELECTOR);
    if (planSelect instanceof HTMLSelectElement) {
      if (planSelect.name === 'selling_plan') {
        planSelect.name = 'recharge_selling_plan_suppressed';
      }
      planSelect.disabled = true;
    }
  }

  /**
   * Watch the document for Recharge widgets being inserted or rebuilt by
   * Recharge's Vue runtime, and re-neutralise on every change.
   */
  function watchForRechargeWidgets() {
    // Initial sweep covers the case where the widget is already in the DOM.
    neutraliseRechargeWidgets();

    const observer = new MutationObserver((mutations) => {
      let shouldRerun = false;

      for (const mutation of mutations) {
        // Any added node could be (or contain) a Recharge widget root.
        if (mutation.addedNodes.length > 0) {
          shouldRerun = true;
          break;
        }
        // Recharge's Vue may also re-render in place — if attributes
        // change on a Recharge element, re-run too.
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element &&
          mutation.target.closest(RECHARGE_ROOT_SELECTOR)
        ) {
          shouldRerun = true;
          break;
        }
      }

      if (shouldRerun) neutraliseRechargeWidgets();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-widget', 'data-template'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForRechargeWidgets, { once: true });
  } else {
    watchForRechargeWidgets();
  }
})();
